/**
 * Selector de terminal/caja física post-login.
 *
 * El cajero entra acá si su `branch_id` está seteado pero el terminal store
 * está vacío. Lista los terminales activos de su branch; al elegir uno, lo
 * persiste en el terminal store y redirige al POS (que de ahí salta a
 * /open-caja si no hay sesión abierta).
 */

import { LogOut, Monitor } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { BrandLockup } from "@/components/BrandMark.tsx";
import { useBranchTerminals } from "@/queries/terminals.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useTerminalStore } from "@/stores/terminal.ts";
import type { TerminalOut } from "@/types/api.ts";

export function SelectTerminalPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearSession);
  const pair = useTerminalStore((s) => s.pair);

  const branchId = user?.branch_id ?? null;
  const terminals = useBranchTerminals(branchId, { onlyActive: true });

  const handleSelect = (terminal: TerminalOut) => {
    pair({
      id: terminal.id,
      code: terminal.code,
      name: terminal.name,
      branchId: terminal.branch_id,
      default_boleta_series_id: terminal.default_boleta_series_id,
      default_factura_series_id: terminal.default_factura_series_id,
      default_nota_venta_series_id: terminal.default_nota_venta_series_id,
    });
    navigate("/", { replace: true });
  };

  const handleLogout = () => {
    clearAuth();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="flex h-20 items-center justify-between border-b border-slate-200 bg-white px-6">
        <BrandLockup caption={`Cajero: ${user?.full_name ?? ""}`} />
        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <LogOut className="h-4 w-4" /> Salir
        </button>
      </header>

      <main className="flex flex-1 flex-col items-center px-6 py-10">
        <div className="w-full max-w-3xl">
          <div className="mb-8 text-center">
            <div className="mb-3 inline-flex rounded-2xl bg-brand-50 p-4 text-brand-700">
              <Monitor className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900">
              ¿Desde qué caja vas a operar?
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              La caja determina qué serie de comprobantes (B001, F001, etc.) se va a usar por
              defecto en cada venta. Una sola persona por caja a la vez.
            </p>
          </div>

          {!branchId && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
              Tu usuario no tiene una sucursal asignada. Pide al administrador que te asigne una
              antes de operar.
            </div>
          )}

          {terminals.isLoading && branchId && (
            <p className="text-center text-slate-500">Cargando cajas…</p>
          )}

          {terminals.isError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              No se pudieron cargar las cajas. Verifica que el servidor de la tienda esté encendido.
            </div>
          )}

          {terminals.data && terminals.data.length === 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800">
              No hay cajas configuradas para tu sucursal. Pide al administrador que cree al menos
              una en Cajas.
            </div>
          )}

          {terminals.data && terminals.data.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {terminals.data.map((t) => (
                <TerminalCard key={t.id} terminal={t} onSelect={handleSelect} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function TerminalCard({
  terminal,
  onSelect,
}: {
  terminal: TerminalOut;
  onSelect: (t: TerminalOut) => void;
}) {
  const hasBoletaDefault = terminal.default_boleta_series_id != null;
  const hasFacturaDefault = terminal.default_factura_series_id != null;
  return (
    <button
      type="button"
      onClick={() => onSelect(terminal)}
      className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-slate-200 bg-white p-6 text-left transition hover:border-brand-300 hover:shadow-md active:scale-[0.98]"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-600 text-sm font-bold text-white">
          {terminal.code.replace(/^CAJA-/i, "") || "?"}
        </div>
        <div>
          <p className="font-mono text-xs text-slate-500">{terminal.code}</p>
          <p className="font-bold text-slate-900">{terminal.name}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <span
          className={
            hasBoletaDefault
              ? "rounded-full bg-brand-50 px-2.5 py-0.5 font-semibold text-brand-800"
              : "rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-500"
          }
        >
          Boleta {hasBoletaDefault ? "✓" : "—"}
        </span>
        <span
          className={
            hasFacturaDefault
              ? "rounded-full bg-brand-50 px-2.5 py-0.5 font-semibold text-brand-800"
              : "rounded-full bg-slate-100 px-2.5 py-0.5 font-semibold text-slate-500"
          }
        >
          Factura {hasFacturaDefault ? "✓" : "—"}
        </span>
      </div>
    </button>
  );
}
